import moment from 'moment-timezone';

// Some browsers return an empty/undefined timeZone when the OS zone isn't recognized by
// their ICU data (e.g. Manitoba 2026). Calling `.tz(undefined)` acts as a getter and returns
// undefined, so fall back to the browser's local offset instead. Zone resolution can also
// throw in some environments, which gets the same fallback.
// ponytail: Chromium on Windows can't map the zones that ended DST on 2026-11-01 to an IANA name
// (Intl timeZone is undefined) and its native Date offsets are stale. Jan/Jul offsets (unaffected
// by the cutover) identify the region; remove once Chromium maps these zones.
//
// Known limits:
// - 360/360 also matches Saskatchewan and Central America/Mexico. They resolve a real Intl zone,
//   so they only land here if Intl returns nothing (then they'd be shown Winnipeg, 1h ahead).
// - An OS update that defines Manitoba as a fixed UTC-5 would report 300/300 (mapped above). The
//   same fixed-offset model for Alberta (360/360) and BC (420/420) collides with the stale
//   Manitoba/Alberta rows, so those updated hosts are not mapped; they keep native Date.
// - 300/300 also matches Bogota/Lima/Jamaica/Panama; they resolve a real Intl zone, so they only
//   land here if Intl returns nothing.
// - Jan/Jul offsets can't tell Manitoba from US Central (likewise Alberta/Denver, BC/Los Angeles).
//   US hosts only reach this when Intl returns no zone, which Chromium doesn't do for US zones. If
//   one does, it is shown the Canadian zone (1h ahead after 2026-11-01).
// - setDefaultTimeZone runs once at boot; if the OS zone changes mid-session, plain moment() keeps
//   the boot zone while tzTime re-resolves per call. A reload fixes it.
const STALE_ZONE_BY_OFFSETS: Record<string, string> = {
  '360/300': 'America/Winnipeg', // Manitoba, host keeps DST rules
  '360/360': 'America/Winnipeg', // Manitoba, measured on Windows DA: unmapped zone = fixed UTC-6
  '300/300': 'America/Winnipeg', // Manitoba, host already updated to a fixed UTC-5 (unverified)
  '420/360': 'America/Edmonton', // Alberta
  '480/420': 'America/Vancouver', // British Columbia
};

const jsOffset = (month: number) => new Date(2026, month, 1).getTimezoneOffset();

// Zones whose host ICU/Date rules may be stale even when Intl names them correctly.
const AFFECTED_ZONES = new Set(Object.values(STALE_ZONE_BY_OFFSETS));

function intlZone(): string | undefined {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    // zone resolution can throw in some environments; caller still tries the offset lookup
    return undefined;
  }
}

// Display zone used by tzTime and setDefaultTimeZone (which also checks whether Intl supplied it).
// Returns undefined when the zone can't be resolved (callers then use local time).
export function resolveTimeZone(): string | undefined {
  const zone = intlZone() || STALE_ZONE_BY_OFFSETS[`${jsOffset(0)}/${jsOffset(6)}`];
  return zone && moment.tz.zone(zone) ? zone : undefined;
}

// Parse a zone-less wall time (e.g. a datetime-local value) in the resolved zone, the inverse of
// tzTime(...).format(). Falls back to native local parsing when the zone is unresolved.
export function parseLocalDateTime(value: string) {
  const zone = resolveTimeZone();
  return zone ? moment.tz(value, zone) : moment(value);
}

export default function tzTime(time: string | Date) {
  const zone = resolveTimeZone();
  return zone ? moment(time).tz(zone) : moment(time);
}

// Make plain `moment()` calls use the bundled tz data instead of the browser's (possibly stale)
// ICU data. Unresolved zone leaves moment on local time. Healthy hosts (Intl resolves a zone
// outside AFFECTED_ZONES) keep native local time.
export function setDefaultTimeZone() {
  const zone = resolveTimeZone();
  if (zone && (!intlZone() || AFFECTED_ZONES.has(zone))) moment.tz.setDefault(zone);
}
